import { AxiosError } from 'axios'
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup,
  User,
  UserCredential
} from 'firebase/auth'

import { LAST_SYNC_LOCAL_USER, useCookie } from '@/libs/cookie'
import { HttpInstance } from '@/libs/http'
import { ABaseService } from '@/libs/service'
import { EHR_LOCAL_USER, EHR_PERMISSIONS, useStorage } from '@/libs/storage'
import { IStaffService, StaffStatus } from '@/modules/channel'
import { useCachedUser, UserRole } from '@/modules/user'
import { clearTimeAtZeroTabs } from '@/utils/autoLogout'
import Logger, { EventName, LogLevel, ProviderType } from '@/wrappers/logger'

import useToken from '../hooks/useToken'
import { auth, signOut } from '../plugins/firebase.plugin'
import IAuthService from './auth.interface'

export default class AuthService extends ABaseService implements IAuthService {
  private staffService: IStaffService

  constructor(clientHttp: HttpInstance, staffService: IStaffService) {
    super(clientHttp)
    this.staffService = staffService
  }

  async login(): Promise<boolean> {
    const provider = new GoogleAuthProvider()

    auth.useDeviceLanguage()

    try {
      const result = await signInWithPopup(auth, provider)
      await this.getUserDataAndSaveToken(result)
      return true
    } catch (error) {
      this.setError({
        message: 'Ocorreu um erro ao realizar o login',
        error,
        request: 'auth/login'
      })
      Logger.addLog(
        EventName.FIREBASE_ERROR,
        {
          data: {
            context: 'Firebase signIn error',
            error: error as Error
          }
        },
        {
          isAction: true,
          logLevel: LogLevel.ERROR
        },
        [ProviderType.DATADOG]
      )
      return false
    }
  }
  async loginWithEmailLink(fullPath: string, email: string): Promise<boolean> {
    if (isSignInWithEmailLink(auth, fullPath)) {
      clearTimeAtZeroTabs()
      try {
        const result = await signInWithEmailLink(auth, email, fullPath)
        await this.getUserDataAndSaveToken(result)
        return true
      } catch (err) {
        // Invalid signIn link, redirect to Health Community Specialist login
        window.location.href = this.getSpecialistLoginUrl()
        return false
      }
    }
    window.location.href = this.getSpecialistLoginUrl()
    return false
  }
  async logout(hasToUpdateStaffStatus = false) {
    const { getUser } = useCachedUser()
    const user = getUser()
    if (hasToUpdateStaffStatus && user) {
      await this.staffService.updateStatus(user.id, StaffStatus.UNAVAILABLE)
    }
    await signOut()
    this.clearAppData()
    const shouldRedirectToHealthCommunitySite =
      user?.role === UserRole.COMMUNITY
    if (shouldRedirectToHealthCommunitySite)
      window.location.href = this.getSpecialistLoginUrl()
    else {
      const redirect = window.location.pathname !== '/login' ? '/login' : null
      if (redirect) window.location.pathname = '/login'
    }
  }
  clearAppData() {
    const { removeToken } = useToken()
    const { removeItem: removePermissions } = useStorage(EHR_PERMISSIONS)
    const { removeItem: removeLocalUser } = useStorage(EHR_LOCAL_USER)
    const { removeUser } = useCachedUser()
    removePermissions()
    removeLocalUser()
    removeUser()
    Logger.removeUser()
    removeToken()
  }
  private getSpecialistLoginUrl() {
    return process.env.VUE_APP_HEALTH_COMMUNITY_SPECIALISTS_AREA_PREFIX_URL
  }
  private async getUserDataAndSaveToken(result: UserCredential) {
    const { setToken } = useToken()
    await this.hydratateToken(result.user)

    const token = await result.user.getIdToken(true)
    const expirationTime = (await result.user.getIdTokenResult()).expirationTime
    setToken(token, expirationTime)
  }
  private async hydratateToken(user: User): Promise<void> {
    if (!user) return

    const { setItem, getItem } = useStorage(EHR_LOCAL_USER)
    const { setCookie, getCookie } = useCookie(LAST_SYNC_LOCAL_USER)
    const localUser = getItem()
    const lastSyncFlag = getCookie()

    if (localUser && lastSyncFlag) return

    try {
      const idToken = await user.getIdToken()

      await this.clientHttp.post('signIn', {
        idToken
      })

      setItem(user)
      setCookie(true, {
        'max-age': 86400
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        const status = e.response?.status

        Logger.addLog(
          EventName.INTERACTION_ERROR,
          {
            data: {
              context: 'signInAPI error, status ' + status,
              error: { messge: e.message }
            }
          },
          {
            isAction: true,
            logLevel: LogLevel.ERROR
          },
          [ProviderType.DATADOG]
        )
      } else if (e instanceof Error) {
        Logger.addLog(
          EventName.INTERACTION_ERROR,
          {
            data: {
              context: 'checkUser error',
              error: { message: e.message }
            }
          },
          {
            isAction: true,
            logLevel: LogLevel.ERROR
          },
          [ProviderType.DATADOG]
        )
      }
      this.logout()
    }
  }
}
